import { BaseProvider } from "./base.provider";

export type ConfigEnv = "staging" | "prod";

export interface ConfigMetadata {
  created_at: string;
  author_id: string;
  commit_message: string;
  rollback_of: number | null;
  promoted_from: number | null;
}

export interface ConfigEntry {
  id: string;
  type: string;
  version: number;
  environment: ConfigEnv;
  payload: Record<string, unknown>;
  public: boolean;
  metadata: ConfigMetadata;
}

export interface PublishConfigPayload {
  payload: Record<string, unknown>;
  commit_message: string;
  public?: boolean;
}

export interface RollbackPayload {
  commit_message: string;
}

export interface PromotePayload {
  commit_message: string;
}

class ConfigsProvider extends BaseProvider {
  listTypes(): Promise<string[]> {
    return this.request<string[]>("/v1/admin/configs");
  }

  async getLatest(type: string, env: ConfigEnv): Promise<ConfigEntry | null> {
    try {
      return await this.request<ConfigEntry>(`/v1/admin/configs/${type}/${env}`);
    } catch (err) {
      if (err instanceof Error && err.message.startsWith("HTTP 404")) return null;
      throw err;
    }
  }

  async getHistory(type: string, env: ConfigEnv, limit = 20): Promise<ConfigEntry[]> {
    try {
      return await this.request<ConfigEntry[]>(
        `/v1/admin/configs/${type}/${env}/history?limit=${limit}`
      );
    } catch (err) {
      if (err instanceof Error && err.message.startsWith("HTTP 404")) return [];
      throw err;
    }
  }

  publish(type: string, env: ConfigEnv, payload: PublishConfigPayload): Promise<ConfigEntry> {
    return this.request<ConfigEntry>(`/v1/admin/configs/${type}/${env}`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  rollback(
    type: string,
    env: ConfigEnv,
    version: number,
    payload: RollbackPayload
  ): Promise<ConfigEntry> {
    return this.request<ConfigEntry>(
      `/v1/admin/configs/${type}/${env}/rollback/${version}`,
      { method: "POST", body: JSON.stringify(payload) }
    );
  }

  promote(type: string, payload: PromotePayload): Promise<ConfigEntry> {
    return this.request<ConfigEntry>(`/v1/admin/configs/${type}/promote`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }
}

export const configsProvider = new ConfigsProvider();
